import * as React from 'react';
import { useState } from 'react';
import { useQuery } from '@apollo/client';

import { ChannelField } from '../ExcludedFieldsPicker.tsx';

import ClientCreateCard from './ClientCreateCard.tsx';
import FormEditorCard from './FormEditorCard.tsx';
import FormRequestsSection from './FormRequestsSection.tsx';
import FormsTable from './FormsTable.tsx';
import { GET_SELF_SERVICE_FORMS, GET_SELF_SERVICE_FORM_CLIENTS } from './queries.ts';

export interface FormRow {
    id: string;
    token: string;
    name: string;
    requestsEnabled: boolean;
    autoConfirm: boolean;
    introText: string;
    excludedFields: string[];
    createdAt: string;
    requestUrl: string;
}

export interface FormClientRow {
    id: string;
    token: string;
    status: 'pending' | 'confirmed' | 'rejected';
    clientCustomerNumber: string;
    clientName: string;
    requesterEmail: string;
    requesterPhone: string;
    requesterMatchType: '' | 'email' | 'domain';
    note: string;
    autoConfirmed: boolean;
    confirmedAt: string | null;
    createdAt: string;
    publicUrl: string;
    form: { id: string; name: string };
}

type CardMode = { type: 'form'; formId?: string } | { type: 'client'; formId: string };

interface FormsConfigProps {
    channelId: string;
    channel?: {
        masterDataConfig?: {
            clientLookupDefinitionId?: string | null;
        } | null;
        documentConfig?: {
            fields?: ChannelField[];
        } | null;
    } | null;
}

const FormsConfig = ({ channelId, channel }: FormsConfigProps) => {
    const clientLookupDefinitionId = channel?.masterDataConfig?.clientLookupDefinitionId ?? null;
    const channelFields = channel?.documentConfig?.fields;

    const [cardMode, setCardMode] = useState<CardMode>({ type: 'form' });

    const formsQuery = useQuery(GET_SELF_SERVICE_FORMS, {
        variables: { channelId },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });
    const clientsQuery = useQuery(GET_SELF_SERVICE_FORM_CLIENTS, {
        variables: { channelId },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    const forms: FormRow[] = formsQuery.data?.selfServiceForms ?? [];
    const clients: FormClientRow[] = clientsQuery.data?.selfServiceFormClients ?? [];
    const pendingRequests = clients.filter((c) => c.status === 'pending');
    const confirmedClients = clients.filter((c) => c.status === 'confirmed');

    const refetchForms = async () => {
        await formsQuery.refetch();
    };
    const refetchClients = async () => {
        await clientsQuery.refetch();
    };
    const refetchAll = async () => {
        await Promise.all([formsQuery.refetch(), clientsQuery.refetch()]);
    };

    const resetCard = () => setCardMode({ type: 'form' });

    const editingForm =
        cardMode.type === 'form' && cardMode.formId ? (forms.find((f) => f.id === cardMode.formId) ?? null) : null;
    const clientCardForm = cardMode.type === 'client' ? (forms.find((f) => f.id === cardMode.formId) ?? null) : null;

    return (
        <div className="flex flex-col gap-10">
            <FormRequestsSection
                requests={pendingRequests}
                clientLookupDefinitionId={clientLookupDefinitionId}
                loading={clientsQuery.loading}
                onChanged={refetchClients}
            />

            <div className="flex flex-col gap-6">
                <FormsTable
                    forms={forms}
                    clients={confirmedClients}
                    loading={formsQuery.loading || clientsQuery.loading}
                    editingFormId={editingForm?.id ?? null}
                    onAddClient={(form) => setCardMode({ type: 'client', formId: form.id })}
                    onEditForm={(form) => setCardMode({ type: 'form', formId: form.id })}
                    onChanged={refetchAll}
                />

                {clientCardForm ? (
                    <ClientCreateCard
                        form={clientCardForm}
                        clientLookupDefinitionId={clientLookupDefinitionId}
                        onClose={resetCard}
                        onChanged={refetchClients}
                    />
                ) : (
                    <FormEditorCard
                        channelId={channelId}
                        channelFields={channelFields}
                        editingForm={editingForm}
                        onCancel={resetCard}
                        onSaved={(formId) => setCardMode({ type: 'form', formId })}
                        onChanged={refetchForms}
                    />
                )}
            </div>
        </div>
    );
};

export default FormsConfig;
